import { REVIEW_TYPES } from '../constants/reviewTypes';
import { getNumericLevel, getLevelDisplayName } from '../constants/skillLevels';

// Helper function to format template type
export const formatTemplateType = (type) => {
  if (!type) return 'N/A';
  
  if (type === "overall") {
    return "Overall";
  }
  
  return type
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, c => c.toUpperCase())
    .trim();
};

// Get reviewer competency levels
export const getReviewerCompetencyLevels = (reviewer) => {
  if (!reviewer?.skillsMatrixTemplate) return [];

  const skillLevels = reviewer.skillsMatrixTemplate.competencyTemplate.levels.map(level => ({
      level: level.level,
      displayName: level.name,
      color: level.color,
      defaultDescription: level.description,
      description: level.description,
      levelId: level.id
  }));

  return skillLevels;
};

// Get color based on competency level
export const getCompetencyColor = (level, skillLevels, reviewType) => {
  if (!skillLevels || skillLevels.length === 0) {
    return '#CCCCCC';
  }

  if (level < 0 || isNaN(level)) {
    return '#CCCCCC';
  }

  const sortedLevels = [...skillLevels]
    .filter(l => reviewType === REVIEW_TYPES.SEQUENTIAL ? l.level >= 0 : l.level > 0)
    .sort((a, b) => a.level - b.level);
  
  if (sortedLevels.length === 0) {
    return '#CCCCCC';
  }
  
  const totalLevels = sortedLevels.length;
  const lowThreshold = Math.ceil(totalLevels / 3);
  const mediumThreshold = Math.ceil((2 * totalLevels) / 3);
  
  const matchingLevel = sortedLevels.find(l => l.level === Math.round(level));
  
  if (matchingLevel && matchingLevel.color) {
    return matchingLevel.color;
  }
  
  if (level <= lowThreshold) {
    return '#f44336';
  } else if (level <= mediumThreshold) {
    return '#ff9800';
  } else {
    return '#4caf50';
  }
};

// Get competency level name from numeric value
export const getCompetencyLevelName = (value, skillLevels) => {
  return getLevelDisplayName(Math.round(value), skillLevels);
};

// Get the maximum gauge value based on valid skill levels
export const getGaugeMaxValue = (skillLevels, reviewType) => {
  if (!skillLevels || skillLevels.length === 0) {
    return 5; // Default fallback
  }

  // Filter valid levels based on review type (same logic as legend)
  const validLevels =
    reviewType === REVIEW_TYPES.SEQUENTIAL ?
      skillLevels.filter(l => l.level >= 0) :
      skillLevels.filter(l => l.level > 0);

  if (validLevels.length === 0) {
    return skillLevels.length;
  }

  // Return the maximum level number from valid levels
  return Math.max(...validLevels.map(l => l.level));
};

// A comment is meaningful when it has at least one non-whitespace character.
// Reviewers have submitted whitespace-only comments (e.g. a single space) to get
// past the required-comment check; those are treated as "no comment provided".
export const hasMeaningfulComment = (comments) => {
  return typeof comments === 'string' && comments.trim().length > 0;
};

// Helper function to check if a skill has any valid comments
export const hasValidComments = (reviewerResponses) => {
  return Array.from(reviewerResponses.values()).some(response =>
    hasMeaningfulComment(response?.comments)
  );
};

// Helper function to check if a segment has any valid comments
export const hasValidSegmentComments = (segmentMappings, segmentId) => {
  return Array.from(segmentMappings.values()).some(mappings => 
    mappings.some(mapping => 
      mapping?.segment?.id === segmentId &&
      hasMeaningfulComment(mapping.comments)
    )
  );
};

// Group responses by skill
export const groupResponsesBySkill = (reportData) => {
  const skillsMap = new Map();

  if (reportData && reportData.reviewers) {
    reportData.reviewers.forEach(reviewer => {
      reviewer.responses?.forEach(response => {
        const skillId = response?.skillsTemplate?.skill?.id;

        if (!skillId) return;

        // Store isRequired flag with the skill data for display purposes
        const isRequired = response?.skillsTemplate?.isRequired !== false;
        
        if (!skillsMap.has(skillId)) {
          skillsMap.set(skillId, {
            skill: response?.skillsTemplate?.skill,
            isRequired,
            reviewerResponses: new Map(),
            segmentMappings: new Map()
          });
        }
        
        skillsMap.get(skillId).reviewerResponses.set(reviewer?.id, response);
        
        if (response?.segmentMappings && response.segmentMappings.length > 0) {
          if (!skillsMap.get(skillId).segmentMappings.has(reviewer?.id)) {
            skillsMap.get(skillId).segmentMappings.set(reviewer?.id, []);
          }
          
          skillsMap.get(skillId).segmentMappings.get(reviewer?.id).push(...response.segmentMappings);
        }
      });
    });
  }
  
  return skillsMap;
};

// Function to calculate competency stats for a reviewer
export const calculateReviewerCompetencyStats = (reviewer, templateTypes, reviewType, isLastReviewer) => {
  const templateType = reviewer.skillsMatrixTemplate?.type;
  if (!templateType) return templateTypes;

  const shouldAddToTemplateStats = !(reviewType === REVIEW_TYPES.SEQUENTIAL && isLastReviewer);

  if (shouldAddToTemplateStats && !templateTypes[templateType]) {
    templateTypes[templateType] = {
      type: templateType,
      totalCompetency: 0,
      count: 0,
      average: 0,
      order: reviewer.order,
    };
  }

  if (!templateTypes["overall"]) {
    templateTypes["overall"] = {
      type: "overall",
      totalCompetency: 0,
      count: 0,
      average: 0,
      order: 999,
    };
  }

  const skillLevels = getReviewerCompetencyLevels(reviewer);

  reviewer.responses?.forEach(response => {
    // Skip non-required skills from calculations
    if (response?.skillsTemplate?.isRequired === false) {
      return;
    }

    // If response has segment mappings, use those competency levels instead
    if (response?.segmentMappings && response.segmentMappings.length > 0) {
      response.segmentMappings.forEach(segmentMapping => {
        if (segmentMapping?.competencyLevelId) {
          const competencyValue = getNumericLevel(segmentMapping.competencyLevelId, skillLevels);
          const shouldConsiderStats = (
            (reviewType === REVIEW_TYPES.PARALLEL && competencyValue > 0)
            || reviewType === REVIEW_TYPES.SEQUENTIAL
          );

          if (shouldAddToTemplateStats) {
            templateTypes[templateType].totalCompetency += competencyValue;
            templateTypes[templateType].count += shouldConsiderStats ? 1 : 0;
          }

          if (reviewType !== REVIEW_TYPES.SEQUENTIAL || isLastReviewer) {
            templateTypes["overall"].totalCompetency += competencyValue;
            templateTypes["overall"].count += shouldConsiderStats ? 1 : 0;
          }
        }
      });
    } else if (response?.competencyLevelId) {
      // Fallback to main response competency level if no segment mappings
      const competencyValue = getNumericLevel(response.competencyLevelId, skillLevels);
      const shouldConsiderStats = (
        (reviewType === REVIEW_TYPES.PARALLEL && competencyValue > 0)
        || reviewType === REVIEW_TYPES.SEQUENTIAL
      );

      if (shouldAddToTemplateStats) {
        templateTypes[templateType].totalCompetency += competencyValue;
        templateTypes[templateType].count += shouldConsiderStats ? 1 : 0;
      }

      if (reviewType !== REVIEW_TYPES.SEQUENTIAL || isLastReviewer) {
        templateTypes["overall"].totalCompetency += competencyValue;
        templateTypes["overall"].count += shouldConsiderStats ? 1 : 0;
      }
    }
  });

  return templateTypes;
};

// Calculate template type statistics
export const calculateTemplateTypeStats = (reportData) => {
  if (!reportData || !reportData.reviewers) return [];

  const templateTypes = {};
  const reviewType = reportData.feedbackSession?.reviewType;

  if (reviewType === REVIEW_TYPES.SEQUENTIAL) {
    const sortedReviewers = [...reportData.reviewers].sort((a, b) => (b.order || 0) - (a.order || 0));
    const lastReviewer = sortedReviewers[0];

    reportData.reviewers.forEach(reviewer => {
      const isLastReviewer = reviewer.id === lastReviewer?.id;
      calculateReviewerCompetencyStats(reviewer, templateTypes, reviewType, isLastReviewer);
    });
  } else {
    reportData.reviewers.forEach(reviewer => {
      calculateReviewerCompetencyStats(reviewer, templateTypes, reviewType, false);
    });
  }

  return Object.values(templateTypes).map(stats => {
    if (stats.count > 0) {
      stats.average = Math.round((stats.totalCompetency / stats.count) * 10) / 10;
    }
    return stats;
  }).sort((a, b) => {
    if (a.type === "overall") return 1;
    if (b.type === "overall") return -1;
    return (a.order || 0) - (b.order || 0);
  });
};

// Calculate overall performance
export const calculateOverallPerformance = (templateTypeStats) => {
  if (!templateTypeStats.length) return { average: 0, count: 0 };
  
  const overallStats = templateTypeStats.find(stats => stats.type === "overall");
  
  if (overallStats) {
    return {
      average: overallStats.average,
      count: overallStats.count
    };
  }
  
  let totalCompetency = 0;
  let totalCount = 0;
  
  templateTypeStats.forEach(stats => {
    totalCompetency += stats.totalCompetency;
    totalCount += stats.count;
  });
  
  return {
    average: totalCount > 0 ? Math.round((totalCompetency / totalCount) * 10) / 10 : 0,
    count: totalCount
  };
}; 